import React, { useCallback, useMemo, useState, useRef } from 'react';
import { List, AutoSizer } from 'react-virtualized';
import type { ListRowProps } from 'react-virtualized';
import type { Fund, Portfolio } from 'venn-api';
import { Icon, ShimmerBlock } from 'venn-ui-kit';
import {
  isStrategyNode,
  recursiveUpdateAssets,
  updateNode,
  findParent,
  normalizePortfolio,
  MANAGE_DATA_SECTION,
  navigateToManageDataPage,
  FS,
  deleteNodeAndEmptyParents,
} from 'venn-utils';
import { cloneDeep, noop } from 'lodash';
import type { History } from 'history';
import AllocationTreeItem from './AllocationTreeItem';
import type { DropPosition } from './AllocationTreeItem';
import { TreeItemUpdateType } from './AllocationUtils';
import type { DeprecatedIncorrectlyDefinedItemPercentageProps } from './ItemAllocation';
import { AllocationPanelContext, type AllocationPanelContextValue } from './AllocationPanelContext';
import { hasData } from './allocation-panel-utils';
import type { AllocationPanelPortfolio } from './types';
import DragOverlay from './drag-and-drop/DragOverlay';
import { placeInvestmentUnderHierarchy } from '../modals/data-uploader/utils/strategyUtils';

const ROW_HEIGHT = 30;

interface FlattenedTreeNode {
  id: number;
  node: AllocationPanelPortfolio;
  depth: number;
  isStrategy: boolean;
  isRoot: boolean;
  parentId: number | undefined;
  hasChildren: boolean;
  path: number[];
  isGhost: boolean;
  compareStrategy?: Portfolio;
  ghostAncestors?: Portfolio[];
  realParentId?: number;
}

function flattenTreeForVirtualization(
  portfolio: AllocationPanelPortfolio,
  collapsedIds: Set<number>,
  allGhostChildren: Map<number, Portfolio[]>,
  allCompareNodes: Map<number, Portfolio>,
  compareLoading: boolean,
  depth = 0,
  parentId?: number,
  path: number[] = [],
  isRoot = true,
  isGhost = false,
  ghostAncestors: Portfolio[] = [],
  realParentId?: number,
): FlattenedTreeNode[] {
  const result: FlattenedTreeNode[] = [];
  const isStrategy = isStrategyNode(portfolio);
  const hasChildren = isStrategy && portfolio.children && portfolio.children.length > 0;
  const isCollapsed = collapsedIds.has(portfolio.id);
  const currentPath = [...path, portfolio.id];
  const compareStrategy = allCompareNodes.get(portfolio.id);

  result.push({
    id: portfolio.id,
    node: portfolio,
    depth,
    isStrategy,
    isRoot,
    parentId,
    hasChildren,
    path: currentPath,
    isGhost,
    compareStrategy,
    ghostAncestors: isGhost ? ghostAncestors : undefined,
    realParentId: isGhost ? realParentId : undefined,
  });

  if (isStrategy && !isCollapsed && portfolio.children) {
    const childCount = portfolio.children.length;
    for (let i = 0; i < childCount; i++) {
      const child = portfolio.children[i];
      result.push(
        ...flattenTreeForVirtualization(
          child!,
          collapsedIds,
          allGhostChildren,
          allCompareNodes,
          compareLoading,
          depth + 1,
          portfolio.id,
          currentPath,
          false,
          isGhost,
          isGhost ? [...ghostAncestors, portfolio as Portfolio] : [],
          isGhost ? realParentId : undefined,
        ),
      );
    }

    // Add "ghost children" if available (items in comparison but not in main portfolio)
    if (!compareLoading && !isGhost) {
      const ghosts = allGhostChildren.get(portfolio.id) || [];
      for (const ghostChild of ghosts) {
        result.push(
          ...flattenTreeForVirtualization(
            ghostChild as AllocationPanelPortfolio,
            collapsedIds,
            allGhostChildren,
            allCompareNodes,
            compareLoading,
            depth + 1,
            portfolio.id,
            currentPath,
            false,
            true,
            [],
            portfolio.id,
          ),
        );
      }
    }
  }

  return result;
}

export interface AllocationTreeProps extends DeprecatedIncorrectlyDefinedItemPercentageProps {
  portfolio: Portfolio;
  selectedStrategyId: number;
  compareLoading: boolean;
  allUpdatedFunds?: Map<string, Fund>;
  allOriginalNodes: Map<number, Portfolio>;
  allCompareNodes: Map<number, Portfolio>;
  allGhostChildren: Map<number, Portfolio[]>;
  hideCompareValue: boolean;
  isTradesView?: boolean;
  onUpdatePortfolio: (updatedPortfolio: Portfolio, updateType: TreeItemUpdateType) => void;
  onSelectStrategy?: (selectedStrategy: Portfolio) => void;
  onDragFinished?: () => void;
  setIsDragging?: (isDragging: boolean) => void;
  updateFund?: (fundId: string) => Promise<Fund | undefined>;
  hasAccessToCompare?: boolean;
  hideComparisonColumn: boolean;
  history: History;
  shouldUseNetAssetsInsteadOfAllocation: boolean;
  shouldAccountForDisplayingPlus?: boolean;
}

type AllocationTreePropsInner = AllocationTreeProps & {
  context: AllocationPanelContextValue;
};

const AllocationTree: React.FC<AllocationTreePropsInner> = ({
  portfolio,
  selectedStrategyId,
  compareLoading,
  allUpdatedFunds,
  allOriginalNodes,
  allCompareNodes,
  allGhostChildren,
  hideCompareValue,
  isTradesView,
  onUpdatePortfolio,
  onSelectStrategy,
  onDragFinished,
  setIsDragging,
  hasAccessToCompare,
  hideComparisonColumn,
  history,
  shouldUseNetAssetsInsteadOfAllocation,
  isPercentageMode,
  baseAllocation,
  orignalBaseAllocation,
  updateFund,
  context,
}) => {
  const [collapsedIds, setCollapsedIds] = useState<Set<number>>(new Set());
  const [draggedNode, setDraggedNode] = useState<Portfolio | undefined>();
  const [dragInitialPos, setDragInitialPos] = useState<{ x: number; y: number } | undefined>();
  const listRef = useRef<List>(null);

  const activePortfolio =
    context.allocationPanelType === 'historical read-only' && context.status === 'success'
      ? context.portfolio
      : portfolio;

  const setNodeCollapsed = useCallback((id: number, collapsed: boolean) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (collapsed) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  const flattenedNodes = useMemo(
    () =>
      flattenTreeForVirtualization(
        activePortfolio as AllocationPanelPortfolio,
        collapsedIds,
        allGhostChildren,
        allCompareNodes,
        compareLoading,
      ),
    [activePortfolio, collapsedIds, allGhostChildren, allCompareNodes, compareLoading],
  );

  const handleDrag = useCallback(
    (event: React.MouseEvent<HTMLElement>, node: Portfolio) => {
      setDraggedNode(node);
      setDragInitialPos({ x: event.clientX, y: event.clientY });
      setIsDragging?.(true);

      const handleMouseUp = () => {
        window.removeEventListener('mouseup', handleMouseUp);
        setDraggedNode(undefined);
        setDragInitialPos(undefined);
        setIsDragging?.(false);
        onDragFinished?.();
      };
      window.addEventListener('mouseup', handleMouseUp);
    },
    [setIsDragging, onDragFinished],
  );

  const handleDrop = useCallback(
    (dropOnNode: Portfolio, positionedAs: DropPosition) => {
      if (!draggedNode) return;

      let updatedPortfolio = cloneDeep(activePortfolio);

      const oldParent = findParent(activePortfolio, draggedNode);
      const oldParentId = oldParent ? oldParent.id : undefined;
      updatedPortfolio = updateNode(updatedPortfolio, oldParentId, (node: Portfolio) => {
        const removeAtIdx = node.children.findIndex((child: Portfolio) => child.id === draggedNode?.id);
        const updatedChildren = [...node.children];
        updatedChildren.splice(removeAtIdx, 1);
        return { ...node, children: updatedChildren };
      });

      const newParent =
        positionedAs === 'FIRST_CHILD' || positionedAs === 'LAST_CHILD'
          ? dropOnNode
          : findParent(activePortfolio, dropOnNode);
      const newParentId = newParent ? newParent.id : undefined;
      updatedPortfolio = updateNode(updatedPortfolio, newParentId, (node: Portfolio) => {
        const addAtIdx =
          positionedAs === 'FIRST_CHILD'
            ? 0
            : positionedAs === 'LAST_CHILD'
              ? node.children.length
              : node.children.findIndex((child: Portfolio) => child.id === dropOnNode.id) + 1;
        const updatedChildren = [...node.children];
        updatedChildren.splice(addAtIdx, 0, draggedNode);
        return { ...node, children: updatedChildren };
      });

      updatedPortfolio = recursiveUpdateAssets(updatedPortfolio);
      updatedPortfolio = normalizePortfolio(updatedPortfolio);
      onUpdatePortfolio(updatedPortfolio, TreeItemUpdateType.CHANGE_ALLOCATION);
    },
    [draggedNode, activePortfolio, onUpdatePortfolio],
  );

  const handleStrategyUpdate = useCallback(
    (updatedStrategy: Portfolio, updateType: TreeItemUpdateType) => {
      let updatedPortfolio = cloneDeep(activePortfolio);
      let finalUpdateType = updateType;

      if (updateType === TreeItemUpdateType.DELETE_PORTFOLIO_CHILD) {
        const parent = findParent(updatedPortfolio, updatedStrategy);
        if (parent) {
          const updatedChildren = parent.children.filter((child: Portfolio) => child.id !== updatedStrategy.id);
          const parentBecomesEmpty = updatedChildren.length === 0;
          const shouldCascadeDeleteParent = parentBecomesEmpty && FS.has('rcm_portfolio_strategy_enforcement_ff');

          if (shouldCascadeDeleteParent && parent.id !== activePortfolio.id) {
            updatedPortfolio = deleteNodeAndEmptyParents(updatedPortfolio, updatedStrategy);
          } else {
            updatedPortfolio = updateNode(updatedPortfolio, parent.id, (node: Portfolio) => ({
              ...node,
              children: updatedChildren,
            }));
            if (parentBecomesEmpty && !shouldCascadeDeleteParent) {
              finalUpdateType = TreeItemUpdateType.DELETE_PORTFOLIO;
            }
          }
        }
      } else {
        updatedPortfolio = updateNode(updatedPortfolio, updatedStrategy.id, () => updatedStrategy);
      }

      updatedPortfolio = recursiveUpdateAssets(updatedPortfolio);
      updatedPortfolio = normalizePortfolio(updatedPortfolio);
      onUpdatePortfolio(updatedPortfolio, finalUpdateType);
    },
    [activePortfolio, onUpdatePortfolio],
  );

  const compareStrategy = compareLoading ? undefined : allCompareNodes.get(activePortfolio.id);
  const compareStrategyAllocation = shouldUseNetAssetsInsteadOfAllocation
    ? compareStrategy?.netAssets
    : compareStrategy?.allocation;

  const onSelectStrategyCallback = useMemo(
    () =>
      onSelectStrategy === undefined
        ? undefined
        : (selected: Portfolio | undefined) => onSelectStrategy(selected ?? activePortfolio),
    [onSelectStrategy, activePortfolio],
  );

  const handleAddChildFromGhost = useCallback(
    (ghostAncestors: Portfolio[], parentId: number | undefined, child: Portfolio) => {
      let updatedPortfolio = cloneDeep(activePortfolio);

      // Build the ghost hierarchy by wrapping the child in each ghost ancestor
      // ghostAncestors is ordered from outermost to innermost (closest to child)
      // Use child.id + offset for each wrapper to ensure unique IDs
      let nodeToAdd: Portfolio = child;
      for (let i = ghostAncestors.length - 1; i >= 0; i--) {
        const ghostAncestor = ghostAncestors[i]!;
        nodeToAdd = {
          ...ghostAncestor,
          children: [nodeToAdd],
          allocation: nodeToAdd.allocation,
          cashBalance: nodeToAdd.cashBalance,
          netAssets: nodeToAdd.netAssets,
          id: nodeToAdd.id + 1,
        };
      }

      // Add to the real parent (parentId points to the real ancestor)
      updatedPortfolio = updateNode(updatedPortfolio, parentId, (node: Portfolio) => ({
        ...node,
        children: [...node.children, nodeToAdd],
      }));

      updatedPortfolio = recursiveUpdateAssets(updatedPortfolio);
      updatedPortfolio = normalizePortfolio(updatedPortfolio);
      onUpdatePortfolio(updatedPortfolio, TreeItemUpdateType.ADD_FROM_GHOST);
    },
    [activePortfolio, onUpdatePortfolio],
  );

  const handlePlaceInvestmentInHierarchy = useCallback(
    (investmentNode: Portfolio, hierarchyLevels: string[], constituentId?: number) => {
      let targetPortfolio = cloneDeep(activePortfolio);

      if (constituentId) {
        targetPortfolio = updateNode(targetPortfolio, constituentId, (constituentNode: Portfolio) => {
          return placeInvestmentUnderHierarchy(constituentNode, hierarchyLevels, investmentNode);
        });
      } else {
        targetPortfolio = placeInvestmentUnderHierarchy(targetPortfolio, hierarchyLevels, investmentNode);
      }

      const finalPortfolio = recursiveUpdateAssets(targetPortfolio);
      onUpdatePortfolio(finalPortfolio, TreeItemUpdateType.ADD_FUND);
    },
    [activePortfolio, onUpdatePortfolio],
  );

  const rowRenderer = useCallback(
    ({ index, key, style }: ListRowProps, totalWidth: number) => {
      const flatNode = flattenedNodes[index];
      if (!flatNode) return null;

      const { node, depth, isRoot, isGhost } = flatNode;
      const isCollapsed = collapsedIds.has(node.id);
      const isSelected = (isRoot && !selectedStrategyId) || node.id === selectedStrategyId;
      const isInSelectedSubtree = flatNode.path.includes(selectedStrategyId);
      const nodeCompareStrategy = isGhost ? node : allCompareNodes.get(node.id);
      const isDragged = !isGhost && draggedNode?.id === node.id;
      const isInDraggedSubtree = !isGhost && draggedNode ? flatNode.path.includes(draggedNode.id) : false;

      return (
        <div
          key={key}
          style={style}
          className={`flex items-stretch ${isGhost ? 'qa-disabled-block' : 'qa-enabled-block'}`}
        >
          {depth > 0 && (
            <div className="flex shrink-0">
              {Array.from({ length: depth }, (_, i) => (
                <div
                  key={i}
                  className="w-[1px] h-[30px] ml-[16px]"
                  style={{
                    backgroundImage:
                      'repeating-linear-gradient(to bottom, rgb(142, 153, 171) 0px, rgb(142, 153, 171) 3px, transparent 3px, transparent 5px)',
                  }}
                />
              ))}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <AllocationTreeItem
              key={`${node.id}-${isGhost}`}
              strategy={isGhost ? undefined : node}
              selectedStrategyId={selectedStrategyId}
              compareStrategy={nodeCompareStrategy}
              compareLoading={compareLoading}
              allOriginalNodes={allOriginalNodes}
              allCompareNodes={allCompareNodes}
              allGhostChildren={allGhostChildren}
              allUpdatedFunds={allUpdatedFunds}
              hideCompareValue={hideCompareValue}
              isRoot={isRoot}
              isInSelectedSubtree={isInSelectedSubtree || isSelected}
              onSelectStrategy={onSelectStrategyCallback}
              onStrategyUpdate={handleStrategyUpdate}
              totalWidth={totalWidth}
              draggedNode={isGhost ? undefined : draggedNode}
              onDrag={isGhost ? noop : handleDrag}
              onDrop={isGhost ? noop : handleDrop}
              onAddChildFromGhost={
                isGhost
                  ? (child) => handleAddChildFromGhost(flatNode.ghostAncestors || [], flatNode.realParentId, child)
                  : undefined
              }
              isInDraggedSubtree={isInDraggedSubtree || isDragged}
              isPercentageMode={isPercentageMode}
              baseAllocation={baseAllocation}
              orignalBaseAllocation={orignalBaseAllocation}
              secondaryTotal={compareStrategyAllocation}
              isTradesView={isTradesView}
              updateFund={updateFund}
              onPlaceInvestmentInHierarchy={handlePlaceInvestmentInHierarchy}
              hasAccessToCompare={hasAccessToCompare}
              hideComparisonColumn={hideComparisonColumn}
              rootName={activePortfolio.name}
              shouldUseNetAssetsInsteadOfAllocation={shouldUseNetAssetsInsteadOfAllocation}
              isCollapsed={isCollapsed}
              onToggleCollapse={(collapsed) => setNodeCollapsed(node.id, collapsed)}
            />
          </div>
        </div>
      );
    },
    [
      flattenedNodes,
      collapsedIds,
      selectedStrategyId,
      allCompareNodes,
      allOriginalNodes,
      allGhostChildren,
      allUpdatedFunds,
      draggedNode,
      hideCompareValue,
      compareLoading,
      compareStrategyAllocation,
      isTradesView,
      handleStrategyUpdate,
      onSelectStrategyCallback,
      handleDrag,
      handleDrop,
      handleAddChildFromGhost,
      handlePlaceInvestmentInHierarchy,
      hasAccessToCompare,
      hideComparisonColumn,
      activePortfolio.name,
      shouldUseNetAssetsInsteadOfAllocation,
      isPercentageMode,
      baseAllocation,
      orignalBaseAllocation,
      updateFund,
      setNodeCollapsed,
    ],
  );

  if (
    context.allocationPanelType === 'historical read-only' &&
    context.status === 'error' &&
    context.errorType === 'insufficient overlap with returns'
  ) {
    return (
      <div className="flex flex-col items-center justify-start h-full text-center p-[12px]">
        <div className="mb-[24px]">
          <Icon prefix="fat" type="circle-x" className="text-[100px] text-venn-grey-30" />
        </div>
        <div>
          Unable to run analysis. There are insufficient returns available to analyze. Please review in{' '}
          <button
            type="button"
            className="border-none font-bold text-venn-dark-blue text-inherit cursor-pointer hover:cursor-pointer disabled:cursor-not-allowed"
            onClick={() =>
              navigateToManageDataPage(
                history,
                { portfolioId: portfolio.id },
                'Studio',
                true,
                MANAGE_DATA_SECTION.ANALYSIS_RANGE,
              )
            }
          >
            Manage Data
          </button>
          .
        </div>
      </div>
    );
  }

  if (context.allocationPanelType === 'historical read-only' && !hasData(context)) {
    return <ShimmerBlock className="[&&]:h-full [&&]:w-full [&&]:mb-0 flex-1" />;
  }

  return (
    <div className="h-[calc(100%-8px)] mt-[8px] relative overflow-hidden [&_.ReactVirtualized__List]:outline-none">
      {draggedNode && dragInitialPos && (
        <DragOverlay initialX={dragInitialPos.x} initialY={dragInitialPos.y} portfolioNode={draggedNode} />
      )}
      <AutoSizer>
        {({ width, height }) => (
          <List
            ref={listRef}
            width={width}
            height={height}
            rowCount={flattenedNodes.length}
            rowHeight={ROW_HEIGHT}
            rowRenderer={(props) => rowRenderer(props, width)}
            overscanRowCount={20}
            style={{ scrollbarGutter: 'stable' }}
          />
        )}
      </AutoSizer>
    </div>
  );
};

const AllocationTreeOuter = (props: AllocationTreeProps) => (
  <AllocationPanelContext.Consumer>
    {(context: AllocationPanelContextValue) => <AllocationTree {...props} context={context} />}
  </AllocationPanelContext.Consumer>
);
export default AllocationTreeOuter;
